import React, {useEffect, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant,
  FormItem,
  InputType,
  useKeyboardShortcut,
  useModal,
  useToast
} from '@fs/uikit-react';
import styles from './CategoryModal.module.css';
import {ApiError} from "@/services/api";
import {CategoriesApi} from "@/shared/api/CategoriesApi.ts";
import {Category} from "@/shared/model/category.ts";

export enum ModalMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

interface CategoryModalProps {
  mode: ModalMode;
  category?: Category;
  onSuccess: () => void;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ mode, category, onSuccess }) => {
  const { closeModal } = useModal();
  const toast = useToast();
  const isEditMode = mode === ModalMode.EDIT;

  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');
  const [color, setColor] = useState(category?.color || '#ffffff');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && category) {
      setName(category.name);
      setDescription(category.description || '');
      setColor(category.color || '#ffffff');
    }
  }, [isEditMode, category]);

  const handleSubmit = async () => {
    if (!name) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, заповніть назву категорії');
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode && category) {
        await CategoriesApi.update(category.id, { name, description, color });
        toast.showSuccessToast('Успішно', 'Категорію оновлено');
      } else {
        await CategoriesApi.create({ name, description, color });
        toast.showSuccessToast('Успішно', 'Категорію створено');
      }
      closeModal();
      onSuccess();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} category:`, error);
      toast.showErrorToast(
        `Помилка ${isEditMode ? 'оновлення' : 'створення'} категорії`,
        getUserFriendlyErrorMessage(error as ApiError)
      );
    } finally {
      setIsLoading(false);
    }
  };

  useKeyboardShortcut('Enter', [], handleSubmit);

  return (
    <div className={styles.modalContent}>
      <FormItem
        label="Назва"
        placeholder="введіть назву"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />

      <FormItem
        label="Опис"
        type={InputType.TEXT_AREA}
        placeholder="введіть опис"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <FormItem
        label="Колір"
        type={InputType.COLOR_PICKER}
        value={color}
        onChange={(e) => setColor(e.target.value)}
      />

      <div className={styles.buttonGroup}>
        <Button
          title={isLoading ? (isEditMode ? 'Збереження...' : 'Створення...') : (isEditMode ? 'Зберегти' : 'Створити')}
          onClick={handleSubmit}
          loading={isLoading}
        />
        <Button
          title="Скасувати"
          onClick={closeModal}
          variant={ButtonVariant.SECONDARY}
        />
      </div>
    </div>
  );
};

export default CategoryModal;
